/**
 * PostFX - cadeia de pos-processamento (secao 4.5 do ARCHITECTURE.md).
 *
 * Monta `this.composer` (lido pelo main.js) com a lib `postprocessing` da pmndrs
 * (6.39). Essa lib NAO tem a mesma API dos exemplos do three: aqui existem
 * `Effect` (funcao mainImage fundida em um unico shader por `EffectPass`) e
 * `Pass`. Efeitos que a versao instalada nao tem - motion blur, correcao de cor,
 * grao - sao escritos como `Effect` proprio, com shader proprio.
 *
 * ORDEM DA CADEIA (a ordem e a razao de ela existir):
 *
 *   1. RenderPass(scene, camera)          mundo em HDR (RGBA16F)
 *   2. ViewModelPass(viewScene,viewCamera) arma em primeira pessoa com o DEPTH
 *                                          LIMPO antes - sem isso a arma entra
 *                                          na parede quando o jogador encosta
 *   3. NormalPass + SSAO                   oclusao de contato e de ambiente
 *   4. Bloom                               limiar calibrado em HDR (ver nota)
 *   5. Motion blur                         reprojecao pela matriz da camera
 *   6. Depth of field                      so na mira (ADS)
 *   7. Exposicao + tone mapping + cor      HDR -> tela, curva S, split tone
 *   8. Vinheta + aberracao + grao          acabamento de lente e filme
 *   9. SMAA (ou FXAA na qualidade baixa)   antialias por ultimo, ja em LDR
 *
 * Por que efeitos de convolucao ficam sozinhos no seu passe: quando varios
 * efeitos sao fundidos, so o primeiro enxerga o buffer de entrada intacto -
 * `EffectPass` ordena por atributo e poe CONVOLUTION na frente. Um efeito que
 * releia `inputBuffer` depois de outro ter alterado a cor acumulada misturaria
 * pixel corrigido com pixel cru (a aberracao cromatica ficaria com o canal verde
 * corrigido e o vermelho nao). Por isso motion blur e DOF tem passe proprio.
 *
 * Regra de ouro deste arquivo: nada aqui pode estourar excecao dentro do frame,
 * e nada aqui aloca objeto por frame (matrizes e vetores sao temporarios de
 * modulo, declarados uma vez).
 */

import * as THREE from 'three';
import {
  AdaptiveLuminancePass,
  BlendFunction,
  BloomEffect,
  ClearPass,
  DepthOfFieldEffect,
  Effect,
  EffectAttribute,
  EffectComposer,
  EffectPass,
  FXAAEffect,
  LuminancePass,
  NormalPass,
  Pass,
  RenderPass,
  SMAAEffect,
  SMAAPreset,
  SSAOEffect,
  EdgeDetectionMode,
  ToneMappingEffect,
  ToneMappingMode
} from 'postprocessing';

// --- temporarios de modulo (ZERO alocacao por frame) -------------------------
const _viewProj = new THREE.Matrix4();
const _prevViewProj = new THREE.Matrix4();
const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _prevQuat = new THREE.Quaternion();

// --- constantes de ajuste ----------------------------------------------------
/** Distancia de foco quando o raycast nao acha nada (mirando o ceu). */
const FOCUS_FALLBACK = 60;
/** Alcance do raycast de foco automatico. */
const FOCUS_RANGE_MAX = 140;
/** Frames iniciais com adaptacao rapida, para o primeiro print ja sair estavel. */
const WARMUP_FRAMES = 30;

// =============================================================================
// Passe da arma em primeira pessoa
// =============================================================================

/**
 * Desenha a `viewScene` por cima do mundo ja renderizado, com o buffer de
 * profundidade zerado antes.
 *
 * Zerar o depth e o ponto todo: a arma usa uma camera propria (near 0.002) e
 * ocupa um espaco fisico que se sobrepoe ao mundo. Sem limpar, a parede a 30 cm
 * do rosto recortaria o cano. Limpando, a arma sempre ganha - que e como todo
 * FPS resolve isso.
 *
 * needsSwap = false  -> desenha no MESMO buffer que o mundo, sem trocar alvo.
 * needsDepthBlit = false -> a textura de profundidade estavel continua sendo a
 * do MUNDO. Se a profundidade da arma fosse copiada para la, o SSAO desenharia
 * um contorno escuro em volta da silhueta da arma (descontinuidade de depth) e
 * o DOF trataria a arma como um objeto a ~10 m. Nenhum dos dois e aceitavel.
 */
class ViewModelPass extends Pass {
  constructor(scene, camera) {
    super('ViewModelPass', scene, camera);
    this.needsSwap = false;
    this.needsDepthBlit = false;
    this.clearPass = new ClearPass(false, true, false); // so o depth
  }

  render(renderer, inputBuffer) {
    const scene = this.scene;
    const camera = this.camera;
    if (!scene || !camera || scene.children.length === 0) return;

    const target = this.renderToScreen ? null : inputBuffer;
    this.clearPass.renderToScreen = this.renderToScreen;
    this.clearPass.render(renderer, inputBuffer);
    renderer.setRenderTarget(target);
    renderer.render(scene, camera);
  }
}

// =============================================================================
// Efeito 1 do passe de saida: aberracao cromatica + exposicao adaptativa
// =============================================================================

const lensExposureFragment = /* glsl */ `
uniform lowp sampler2D luminanceBuffer;
uniform float exposure;
uniform float autoAmount;
uniform float exposureKey;
uniform float caStrength;

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {

  // --- aberracao cromatica -------------------------------------------------
  // Deslocamento RADIAL, nulo no centro e sub-pixel na borda. Lente boa nao
  // separa cor no meio do quadro; aberracao visivel no centro e o erro que
  // denuncia projeto amador.
  //
  // A rampa comeca em r2 = 0.62 (|uv - 0.5| ~ 0.39): os ~60% centrais do quadro
  // ficam INTOCADOS. Com a rampa antiga (0.32 -> 1.30) o fator ja estava cravado
  // em 1.0 a 5% da borda, ou seja a aberracao maxima cobria quase o quadro todo.
  vec2 d = uv - vec2(0.5);
  float r2 = dot(d, d) * 4.0;
  float edge = smoothstep(0.62, 1.60, r2);
  vec2 off = d * (caStrength * edge);

  // Limitador de contraste local. Este efeito le o buffer HDR CRU: um ceu de
  // radiancia ~0.7 encostando numa silhueta de ~0.005 tem contraste de 140:1, e
  // meio pixel de deslocamento basta para pintar uma franja SATURADA em cima da
  // silhueta. Prendendo a amostra deslocada perto da faixa de cor do proprio
  // pixel, a franja continua existindo (a borda ainda separa cor) mas nasce
  // dessaturada, que e como lente real se comporta.
  float mx = max(max(inputColor.r, inputColor.g), inputColor.b);
  float mn = min(min(inputColor.r, inputColor.g), inputColor.b);
  float lo = mn * 0.65 - 0.02;
  // O max() nao e enfeite: clamp() com limite inferior maior que o superior e
  // comportamento INDEFINIDO em GLSL, e um pixel negativo no HDR inverteria os
  // dois. Custa uma instrucao e elimina a chance de artefato de driver.
  float hi = max(mx * 1.35 + 0.03, lo);

  vec3 color = vec3(
    clamp(texture2D(inputBuffer, uv + off).r, lo, hi),
    inputColor.g,
    clamp(texture2D(inputBuffer, uv - off).b, lo, hi)
  );

  // --- exposicao -----------------------------------------------------------
  // A luminancia media vem de um alvo 1x1 adaptado na GPU (sem leitura de
  // volta para a CPU, que travaria o pipeline). O ganho e limitado de proposito:
  // autoexposicao larga faz a imagem "respirar" e denuncia o truque.
  //
  // A faixa e ESTREITA por decisao de projeto: 0.88 a 1.14 da 0.37 stop de ponta
  // a ponta (a faixa antiga, 0.74 a 1.36, dava 0.87 stop). Com autoAmount 0.32 o
  // ganho efetivo fica entre 0.962 e 1.045 - menos de 0.13 stop. Sem isso, virar
  // a cabeca para um beco escuro clareava o mapa inteiro e virar para o poente o
  // escurecia, e cada ponto de vista parecia um horario diferente do dia.
  float lumAvg = unpackRGBAToFloat(texture2D(luminanceBuffer, vec2(0.5)));
  lumAvg = clamp(lumAvg, 0.035, 1.6);
  float autoExposure = clamp(exposureKey / lumAvg, 0.88, 1.14);

  outputColor = vec4(color * (exposure * mix(1.0, autoExposure, autoAmount)), inputColor.a);

}
`;

/**
 * Exposicao com adaptacao lenta + aberracao cromatica de borda.
 *
 * Precisa ser CONVOLUTION porque le o buffer de entrada em dois offsets. Por
 * isso ele e sempre o PRIMEIRO efeito do passe (o EffectPass ordena assim), o
 * que tambem e o correto: aberracao e exposicao acontecem na lente/sensor,
 * antes de qualquer correcao de cor.
 *
 * A luminancia media e produzida pelo mesmo par de passes que o ToneMappingEffect
 * adaptativo da lib usa: LuminancePass (mipmap ate 1x1) + AdaptiveLuminancePass
 * (interpolacao temporal na GPU).
 */
class LensExposureEffect extends Effect {
  constructor({ exposure = 1.0, autoAmount = 0.32, exposureKey = 0.26, caStrength = 0.0022 } = {}) {
    super('LensExposureEffect', lensExposureFragment, {
      attributes: EffectAttribute.CONVOLUTION,
      blendFunction: BlendFunction.SRC,
      uniforms: new Map([
        ['luminanceBuffer', new THREE.Uniform(null)],
        ['exposure', new THREE.Uniform(exposure)],
        ['autoAmount', new THREE.Uniform(autoAmount)],
        ['exposureKey', new THREE.Uniform(exposureKey)],
        ['caStrength', new THREE.Uniform(caStrength)]
      ])
    });

    this.renderTargetLuminance = new THREE.WebGLRenderTarget(1, 1, {
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false
    });
    this.renderTargetLuminance.texture.generateMipmaps = true;
    this.renderTargetLuminance.texture.name = 'Blackout.Luminance';

    this.luminancePass = new LuminancePass({ renderTarget: this.renderTargetLuminance });
    this.adaptiveLuminancePass = new AdaptiveLuminancePass(this.luminancePass.texture, {
      minLuminance: 0.012,
      adaptationRate: 1.0
    });
    this.uniforms.get('luminanceBuffer').value = this.adaptiveLuminancePass.texture;

    // 256x256 -> mip 8 e o 1x1 com a media da cena.
    this._setLuminanceResolution(256);
  }

  _setLuminanceResolution(size) {
    const exponent = Math.max(0, Math.ceil(Math.log2(size)));
    const pow2 = Math.pow(2, exponent);
    this.luminancePass.resolution.setPreferredSize(pow2, pow2);
    this.adaptiveLuminancePass.fullscreenMaterial.mipLevel1x1 = exponent;
  }

  /** Velocidade da adaptacao, em "por segundo". Baixo = lento e sem pulsar. */
  set adaptationRate(value) {
    this.adaptiveLuminancePass.fullscreenMaterial.adaptationRate = value;
  }

  get adaptationRate() {
    return this.adaptiveLuminancePass.fullscreenMaterial.adaptationRate;
  }

  update(renderer, inputBuffer, deltaTime) {
    this.luminancePass.render(renderer, inputBuffer);
    this.adaptiveLuminancePass.render(renderer, null, null, deltaTime);
  }

  initialize(renderer, alpha, frameBufferType) {
    this.adaptiveLuminancePass.initialize(renderer, alpha, frameBufferType);
  }
}

// =============================================================================
// Efeito 3 do passe de saida: correcao de cor, vinheta e grao
// =============================================================================

const filmGradeFragment = /* glsl */ `
uniform float gradeContrast;
uniform float gradeLift;
uniform float gradeSaturation;
uniform float vignetteAmount;
uniform float grainAmount;
uniform vec2 grainOffset;

// Hash barato e sem padrao visivel na tela. Nome com prefixo do projeto para
// nao colidir com funcao de outro efeito fundido no mesmo shader.
float blackoutGrainHash(const in vec2 p) {
  vec3 q = fract(vec3(p.x, p.y, p.x) * vec3(443.8975, 397.2973, 491.1871));
  q += dot(q, q.yzx + 19.19);
  return fract((q.x + q.y) * q.z);
}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {

  // A entrada ja passou pelo tone mapping, entao esta em [0,1]. O clamp e
  // cinto de seguranca: com valor acima de 1 a curva S abaixo deixaria de ser
  // monotonica e inverteria tom nas altas luzes.
  vec3 c = clamp(inputColor.rgb, vec3(0.0), vec3(1.0));

  // Trabalha em espaco perceptual (gama ~2.2): curva e saturacao aplicadas em
  // linear escurecem os meios-tons e achatam a imagem.
  vec3 g = pow(c, vec3(0.4545454545));

  // Curva S ancorada em 0.5. smoothstep e monotonica: nao inverte tom nem
  // cria degrau, so aumenta o contraste no meio e segura as pontas.
  vec3 s = g * g * (3.0 - 2.0 * g);
  g = mix(g, s, gradeContrast);

  // Lift: preto de filme, nunca zero absoluto. E o que tira o aspecto "render".
  g = g * (1.0 - gradeLift) + gradeLift;

  float l = dot(g, vec3(0.2126, 0.7152, 0.0722));

  // Split tone de fim de tarde: sombra puxando para o azul frio do ceu, alta
  // luz puxando para o ambar do sol baixo.
  vec3 tint = mix(vec3(0.962, 0.984, 1.058), vec3(1.048, 1.006, 0.940), smoothstep(0.12, 0.82, l));
  g *= tint;

  // Saturacao MAIOR na sombra, nao menor. A versao anterior tirava 30% da croma
  // em toda a faixa 0.06-0.55 de luminancia perceptual - que e exatamente onde
  // mora a sombra externa deste mapa - alegando que a cor da sombra "vem do ceu,
  // quase acromatica". Isso desmontava em uma linha o trabalho do Lighting.js, que
  // gastou o HEMI_GROUND_LEVEL inteiro justamente para a sombra sair AZUL de
  // verdade. Medido: caixote em sombra com B-R +2.4 contra piso ao lado com +27.2.
  // Sombra de fim de tarde tem MAIS croma que a alta luz (a luz direta e quase
  // branca de tao forte; a sombra e iluminada so pelo ceu). O split tone acima ja
  // empurra a sombra para o azul frio - aqui a saturacao acompanha em vez de brigar.
  float sat = mix(gradeSaturation * 1.12, gradeSaturation * 0.96, smoothstep(0.10, 0.60, l));
  g = mix(vec3(l), g, sat);

  // Vinheta levemente oval, comecando so depois da metade do raio.
  vec2 vd = (uv - vec2(0.5)) * vec2(1.0, 0.92);
  float r = length(vd) * 1.9;
  g *= 1.0 - vignetteAmount * smoothstep(0.55, 1.30, r);

  // Grao de filme: um pixel de tamanho, deslocado a cada frame (grao parado
  // parece sujeira na lente). O envelope concentra o grao nos meios-tons baixos:
  // smoothstep(0.02, 0.14, l) o remove do preto sem informacao, enquanto a rampa
  // inversa ate 0.90 preserva textura na sombra aberta sem sujar a alta luz.
  float n = blackoutGrainHash(uv * resolution + grainOffset);
  float grainWeight = 0.35 + 0.65 * (1.0 - smoothstep(0.10, 0.90, l)) * smoothstep(0.02, 0.14, l);
  g += (n - 0.5) * grainAmount * grainWeight;

  outputColor = vec4(pow(max(g, vec3(0.0)), vec3(2.2)), inputColor.a);

}
`;

class FilmGradeEffect extends Effect {
  constructor({
    contrast = 0.26,
    lift = 0.016,
    saturation = 1.02,
    vignette = 0.20,
    grain = 0.030
  } = {}) {
    super('FilmGradeEffect', filmGradeFragment, {
      blendFunction: BlendFunction.SRC,
      uniforms: new Map([
        ['gradeContrast', new THREE.Uniform(contrast)],
        ['gradeLift', new THREE.Uniform(lift)],
        ['gradeSaturation', new THREE.Uniform(saturation)],
        ['vignetteAmount', new THREE.Uniform(vignette)],
        ['grainAmount', new THREE.Uniform(grain)],
        ['grainOffset', new THREE.Uniform(new THREE.Vector2())]
      ])
    });
  }
}

// =============================================================================
// Motion blur por movimento da camera
// =============================================================================

const motionBlurFragment = /* glsl */ `
uniform mat4 inverseViewProjection;
uniform mat4 previousViewProjection;
uniform float blurScale;
uniform float maxBlur;

void mainImage(const in vec4 inputColor, const in vec2 uv, const in float depth, out vec4 outputColor) {

  outputColor = inputColor;
  if (blurScale <= 0.0005) return;

  // Reconstroi o ponto do mundo a partir do depth e reprojeta com a matriz do
  // frame anterior. Isso da o vetor de velocidade correto tanto para rotacao
  // quanto para translacao, e de graca para o ceu (que fica no plano distante).
  vec4 clip = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
  vec4 world = inverseViewProjection * clip;
  if (abs(world.w) < 1e-6) return;
  world.xyz /= world.w;

  vec4 prev = previousViewProjection * vec4(world.xyz, 1.0);
  if (prev.w <= 0.0) return;

  vec2 prevUv = (prev.xy / prev.w) * 0.5 + 0.5;
  vec2 velocity = (uv - prevUv) * blurScale;

  float len = length(velocity);
  if (len < 1e-5) return;
  // Teto de rastro: sem ele, um giro rapido vira borrao de foto tremida.
  if (len > maxBlur) velocity *= maxBlur / len;

  vec4 sum = vec4(0.0);
  for (int i = 0; i < MB_SAMPLES; ++i) {
    float t = float(i) / float(MB_SAMPLES - 1) - 0.5;
    sum += texture2D(inputBuffer, uv + velocity * t);
  }

  outputColor = sum / float(MB_SAMPLES);

}
`;

class CameraMotionBlurEffect extends Effect {
  constructor({ samples = 9, maxBlur = 0.022 } = {}) {
    super('CameraMotionBlurEffect', motionBlurFragment, {
      attributes: EffectAttribute.CONVOLUTION | EffectAttribute.DEPTH,
      blendFunction: BlendFunction.SRC,
      defines: new Map([['MB_SAMPLES', samples.toFixed(0)]]),
      uniforms: new Map([
        ['inverseViewProjection', new THREE.Uniform(new THREE.Matrix4())],
        ['previousViewProjection', new THREE.Uniform(new THREE.Matrix4())],
        ['blurScale', new THREE.Uniform(0)],
        ['maxBlur', new THREE.Uniform(maxBlur)]
      ])
    });
  }

  set samples(value) {
    this.defines.set('MB_SAMPLES', Math.max(3, Math.round(value)).toFixed(0));
    this.setChanged();
  }
}

// =============================================================================
// Sistema
// =============================================================================

export class PostFX {
  constructor(ctx) {
    this.ctx = ctx;
    /** @type {EffectComposer|null} - lido pelo main.js */
    this.composer = null;

    this._passes = [];
    this._screenCandidates = [];
    this._offSettings = null;
    this._warned = false;
    this._frames = 0;

    // estado suavizado (tudo escalar; nada disso aloca)
    this._ads = 0;
    this._blurGate = 0;
    this._focus = FOCUS_FALLBACK;
    this._focusTarget = FOCUS_FALLBACK;
    this._focusTimer = 0;
    this._hasPrevFrame = false;
  }

  async init() {
    const { renderer, scene, camera, viewScene, viewCamera, settings } = this.ctx;

    // RGBA16F: bloom, exposicao e tone mapping precisam de valores acima de 1.0.
    // Em 8 bits o ceu satura e o bloom vira uma mancha chapada.
    this.composer = new EffectComposer(renderer, {
      frameBufferType: THREE.HalfFloatType,
      multisampling: 0,
      stencilBuffer: false,
      depthBuffer: true
    });
    // O controle de quem escreve na tela e manual: passes sao ligados e
    // desligados durante o jogo (motion blur so em movimento, DOF so na mira) e
    // o automatico da lib marcaria o ultimo passe da lista, mesmo desligado.
    this.composer.autoRenderToScreen = false;

    // --- 1. mundo ------------------------------------------------------------
    this.worldPass = new RenderPass(scene, camera);

    // --- 2. arma em primeira pessoa -----------------------------------------
    this.viewModelPass = new ViewModelPass(viewScene, viewCamera);

    // --- 3. SSAO -------------------------------------------------------------
    // O buffer de NORMAIS continua em meia resolucao (ele custa uma travessia
    // inteira da cena; a normal e de baixa frequencia e o upsampling ciente de
    // profundidade recupera a borda). O buffer de AO e que sobe para resolucao
    // cheia em high/ultra - ver _applySettings.
    this.normalPass = new NormalPass(scene, camera, { resolutionScale: 0.5 });
    this.ssaoEffect = new SSAOEffect(camera, this.normalPass.texture, {
      blendFunction: BlendFunction.MULTIPLY,
      depthAwareUpsampling: true,
      samples: 12,
      rings: 7,
      // ATENCAO: `radius` desta lib NAO e em metros. Em SSAOMaterial.updateRadius()
      // ela faz `RADIUS = r * resolution.height` e usa isso como offset em TEXELS
      // do buffer de AO, ou seja o raio e uma FRACAO DA ALTURA DA TELA (o padrao
      // da lib e 0.1825).
      //
      // 0.25 era grande demais e a aposta de "um kernel so cobre as duas escalas"
      // NAO se sustentou na medicao. Duas razoes, as duas conferidas no fonte
      // instalado (node_modules/postprocessing/build/index.js, getAmbientOcclusion):
      //
      //  1. A amostra MAIS INTERNA fica em alpha = (0+0.5)/samples do raio. Com
      //     0.25 numa captura de 900 px de altura, RADIUS = 225 px e a amostra
      //     interna caia a 9.4 px do pixel sombreado. Contato de parede/chao e
      //     base de caixote vivem ABAIXO de 9 px - simplesmente nao eram
      //     amostrados. Medido na revisao: calcada colada na parede mais CLARA
      //     que a calcada a 2 m.
      //  2. O termo de queda por distancia esta morto nesta lib:
      //     `f = max(RADIUS_SQ - vv, 0) / RADIUS_SQ` compara RADIUS_SQ (texels ao
      //     quadrado, 50625) com vv (metros ao quadrado, ordem de 1). f da 1.0
      //     sempre. Ou seja o raio grande nao produzia gradiente nenhum - so
      //     espalhava um escurecimento chapado.
      //
      // Com 0.06: RADIUS = 54 px a 900p, amostra interna a 2.25 px (~2.6 cm de
      // mundo a 10 m) e externa a 52 px (~0.6 m). O minRadiusScale abaixo segura
      // o tamanho de MUNDO do kernel entre ~0.1 e ~0.6 m em toda a faixa util, que
      // e a escala do contato.
      //
      // Sobre intensity: o estimador desta lib (`vn / (fade + vv)`) NAO e
      // normalizado pelo raio, entao encolher o kernel ja multiplica a oclusao de
      // contato. Por isso o ganho subiu pouco (1.6 -> 1.9) em vez dos 2.2-2.6
      // pedidos: alem disso o corte de canto interno virava preto chapado, que e
      // tao denunciador quanto a ausencia de AO.
      //
      // Um SEGUNDO SSAO para a oclusao larga (junta parede/teto) foi avaliado e
      // DESCARTADO: SSAOEffect nao e um efeito fundido barato - cada instancia tem
      // alvo de render e ShaderPass proprios, entao seriam dois preenchimentos de
      // tela cheia por frame. Com o custo ja fora do alvo do contrato, nao paga.
      radius: 0.06,
      intensity: 1.9,
      // bias e em METROS, subtraido de dot(v, n). Corta as amostras rasas demais,
      // que sao ruido vindo da normal em meia resolucao. 0.015 ainda anulava parte
      // da faixa de contato agora que o kernel mede centimetros; 0.010 mantem a
      // margem contra acne sem comer a sombra de contato.
      bias: 0.010,
      fade: 0.01,
      // luminanceInfluence faz `ao = mix(ao, 0.0, luminancia * influence)` - e a
      // luminancia lida aqui e a do buffer HDR CRU, antes da exposicao e do tone
      // mapping. Com 0.55, qualquer superficie acima de ~1.8 de radiancia ficava
      // com AO ZERO. 0.12 preserva a intencao (nao sujar fonte de luz) sem apagar
      // o reboco batido de sol.
      luminanceInfluence: 0.12,
      minRadiusScale: 0.12,
      // Corta o efeito ao longe (onde vira ruido) e nas descontinuidades
      // grandes de profundidade - e o que evita o contorno escuro nas bordas.
      worldDistanceThreshold: 45,
      worldDistanceFalloff: 8,
      // Amostra com diferenca de profundidade acima do limiar e DESCARTADA como
      // oclusor. Com o kernel de 0.25 esse limiar precisava ser largo (2.5 m)
      // porque o kernel varria metros de profundidade - e ai ele deixava de ser
      // filtro e virava o UNICO gate do efeito (ver a nota do radius: a queda por
      // distancia esta morta). Com o kernel de contato o alcance real nao passa de
      // ~0.6 m, entao 0.9 m ja e folga: segura o halo em recorte de silhueta sem
      // matar o encontro parede/piso visto de esguelha.
      worldProximityThreshold: 0.9,
      worldProximityFalloff: 0.4,
      resolutionScale: 1.0
    });
    this.ssaoPass = new EffectPass(camera, this.ssaoEffect);

    // --- 4. bloom ------------------------------------------------------------
    // O limiar e medido no buffer HDR CRU, e 1.0 ali NAO e "o branco da tela".
    // O ACES do three (tonemapping_pars_fragment) comeca com
    // `color *= toneMappingExposure / 0.6`, ou seja pre-multiplica por 1.667:
    // uma radiancia de ~0.72 ja sai na tela como sRGB ~219. Com o Sky em
    // SKY_LUMINANCE 0.28, o ceu claro deste mapa vive na casa de 0.7 - abaixo do
    // limiar antigo de 1.0. Resultado medido na revisao: o ceu estourado nao
    // sangrava UM pixel sequer na silhueta escura, porque so o disco solar
    // passava do limiar, e ali a imagem ja esta clipada.
    //
    // 0.62 com suavizacao 0.28 deixa o ceu comum entrar com peso baixo (~0.20) e
    // o ceu junto do sol entrar cheio, que e a distribuicao certa: halo forte no
    // contraluz, brilho discreto no resto. Mistura ADD porque antes do tone
    // mapping luz se soma - SCREEN se comporta mal com valores acima de 1.
    //
    // O LIMIAR nao era o problema da rodada 2 - o GANHO e o RAIO eram. Medido no
    // print 91-combate-limpo, saindo do ceu para a silhueta escura: 193, 193, 194,
    // 192, 194, 193, 153, 34, 32, 26, 14. Alta luz encostando em sombra em cinco
    // pixels, sem um fio de halo. Duas causas, as duas no MipmapBlurPass instalado:
    //
    //  - levels 7: a piramide descia ate ~12x7 px. A energia de uma feicao pequena
    //    era espalhada por meia tela e nao sobrava brilho local em lugar nenhum.
    //    Com 5 niveis o nivel mais grosso fica em ~50x29 px, ou seja um halo da
    //    ordem de 60 px - visivel, e ainda curto o bastante para nao lavar o quadro.
    //  - radius 0.72: no shader de upsampling o radius e o peso do
    //    `mix(baseColor, blurDoNivelMaisGrosso, radius)`. 0.72 jogava 72% do
    //    resultado no nivel mais grosso em CADA degrau, ou seja halo longo e
    //    fraquissimo. 0.55 devolve peso ao nivel fino: halo mais curto e mais forte.
    //
    // Com o raio concentrado, intensity 0.55 -> 1.1 levanta a borda sem tocar em
    // quem esta a 30 px dela. Reboco batido de sol continua fora: a radiancia dele
    // fica na casa de 0.53, abaixo do limiar - so ceu, disco solar e especular
    // cravado entram.
    this.bloomEffect = new BloomEffect({
      blendFunction: BlendFunction.ADD,
      luminanceThreshold: 0.62,
      luminanceSmoothing: 0.28,
      mipmapBlur: true,
      intensity: 1.1,
      radius: 0.55,
      levels: 5
    });
    this.bloomPass = new EffectPass(camera, this.bloomEffect);

    // --- 5. motion blur ------------------------------------------------------
    this.motionBlurEffect = new CameraMotionBlurEffect({ samples: 9, maxBlur: 0.022 });
    this.motionBlurPass = new EffectPass(camera, this.motionBlurEffect);

    // --- 6. depth of field ---------------------------------------------------
    this.dofEffect = new DepthOfFieldEffect(camera, {
      focusDistance: FOCUS_FALLBACK,
      focusRange: 14,
      bokehScale: 1.4,
      resolutionScale: 0.5
    });
    this.dofPass = new EffectPass(camera, this.dofEffect);

    // --- 7 e 8. saida: exposicao, tone mapping, cor, lente, grao -------------
    // Os tres cabem no mesmo passe (so um deles e de convolucao) e viram um
    // shader unico. O ToneMappingEffect da lib usa a implementacao ACES do
    // proprio three, que e a curva certa para uma cena de sol baixo e quente.
    this.lensEffect = new LensExposureEffect({
      exposure: 1.0,
      // Chave de exposicao calibrada para cena externa de fim de tarde: a
      // luminancia media medida fica por volta de 0.4 (ceu claro somado a
      // asfalto e concreto na sombra). O peso baixo e o intervalo apertado
      // garantem que a imagem nunca vai estourar nem apagar sozinha.
      exposureKey: 0.42,
      // Peso baixo de proposito: com 0.55 e a faixa larga do clamp antigo, o
      // enquadramento comandava a exposicao e cada ponto de vista do mapa
      // parecia um horario diferente. Em 0.32, o ganho anda entre 0.962 e 1.045
      // (0.13 stop) - adaptacao suficiente para o olho, pequena demais para
      // desmontar a nocao de que existe uma unica iluminacao no mapa.
      autoAmount: 0.32,
      // ~0.35 pixel de deslocamento por canal no canto de uma tela 1080p, e
      // ainda com limitador de contraste no shader. Aberracao percebida
      // conscientemente ja e aberracao demais.
      caStrength: 0.00045
    });
    this.toneMappingEffect = new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC });
    this.gradeEffect = new FilmGradeEffect({
      contrast: 0.22,
      lift: 0.016,
      saturation: 1.02,
      vignette: 0.20,
      grain: 0.030
    });
    this.outputPass = new EffectPass(camera, this.lensEffect, this.toneMappingEffect, this.gradeEffect);

    // --- 9. antialias --------------------------------------------------------
    // Os dois existem sempre; so um fica ligado. Trocar efeito dentro de um
    // passe obrigaria a recompilar o shader no meio da partida.
    this.smaaEffect = new SMAAEffect({
      preset: SMAAPreset.HIGH,
      edgeDetectionMode: EdgeDetectionMode.COLOR
    });
    this.smaaPass = new EffectPass(camera, this.smaaEffect);
    this.fxaaEffect = new FXAAEffect();
    this.fxaaPass = new EffectPass(camera, this.fxaaEffect);

    this._passes = [
      this.worldPass,
      this.viewModelPass,
      this.normalPass,
      this.ssaoPass,
      this.bloomPass,
      this.motionBlurPass,
      this.dofPass,
      this.outputPass,
      this.smaaPass,
      this.fxaaPass
    ];
    // Passes que podem ser o ultimo da fila (o que escreve na tela). O mundo, a
    // arma e o buffer de normais nunca entram: eles nao produzem imagem final.
    this._screenCandidates = [
      this.ssaoPass,
      this.bloomPass,
      this.motionBlurPass,
      this.dofPass,
      this.outputPass,
      this.smaaPass,
      this.fxaaPass
    ];
    for (let i = 0; i < this._passes.length; i++) this.composer.addPass(this._passes[i]);

    // Dithering no ultimo passe: quebra o banding do degrade do ceu, que e o
    // artefato mais visivel de um render de 8 bits na tela.
    this.smaaPass.dithering = true;
    this.fxaaPass.dithering = true;

    // Adaptacao rapida nos primeiros frames para a imagem ja nascer estavel
    // (a revisao visual tira print poucos frames depois de posicionar a camera).
    this.lensEffect.adaptationRate = 12;

    this._applySettings();
    this._offSettings = this.ctx.settings.onChange(() => this._applySettings());

    // Aquecimento (ver WARMUP_FRAMES em _updateCameraMotion e _updateFocus): os
    // passes condicionais nascem ligados, mas com parametro neutro (blurScale e
    // bokehScale em zero), so para o driver compilar esses shaders durante o
    // carregamento. Sem isso, a primeira mira e o primeiro giro rapido travam
    // meio segundo cada um, bem no meio do tiroteio.
    this.motionBlurPass.enabled = !!settings.motionBlur;
    this.dofPass.enabled = !!settings.depthOfField;
    this.dofEffect.bokehScale = 0;
    this._syncRenderToScreen();
  }

  // ---------------------------------------------------------------------------
  // Frame
  // ---------------------------------------------------------------------------

  update(dt) {
    // Zera os contadores do renderer uma vez por frame. O Renderer.js desliga
    // info.autoReset justamente para isto: este update roda logo antes do
    // render, entao __GAME__.stats() enxerga o frame inteiro e nao so o ultimo
    // passe de tela cheia. Fica fora do try e antes de qualquer saida
    // antecipada: se o pos-processamento cair, a estatistica continua valida.
    const info = this.ctx.renderer && this.ctx.renderer.info;
    if (info && typeof info.reset === 'function') info.reset();

    if (!this.composer) return;
    try {
      this._frames++;
      if (this._frames === WARMUP_FRAMES) {
        // Depois do aquecimento a exposicao passa a andar devagar, que e o que
        // faz a adaptacao parecer olho humano e nao ganho automatico de camera.
        this.lensEffect.adaptationRate = 2.2;
      }

      const step = dt > 0 ? dt : 1 / 60;
      this._updateAds(step);
      this._updateCameraMotion(step);
      this._updateFocus(step);
      this._updateGrain();
    } catch (err) {
      this._warnOnce(err);
    }
  }

  /** Quanto o jogador esta na mira, de 0 a 1, com transicao suave. */
  _updateAds(dt) {
    const weapons = this.ctx.weapons;
    const target = weapons && weapons.isADS ? 1 : 0;
    this._ads += (target - this._ads) * Math.min(1, dt * 9);
    if (this._ads < 0.0005) this._ads = 0;
  }

  /**
   * Matrizes de reprojecao e forca do motion blur.
   *
   * A camera e atualizada pelo CameraRig antes deste sistema (ordem definida no
   * main.js), entao aqui ela ja esta na pose final do frame.
   */
  _updateCameraMotion(dt) {
    const camera = this.ctx.camera;
    if (!camera) return;

    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

    _prevViewProj.copy(_viewProj);
    _viewProj.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);

    // Velocidade angular em rad/s: e ela que decide se ha rastro. Blur ligado
    // com a camera parada borra print e mata o detalhe do cenario.
    let angular = 0;
    if (this._hasPrevFrame) {
      angular = _prevQuat.angleTo(camera.quaternion) / dt;
    }
    _prevQuat.copy(camera.quaternion);

    const wantBlur = !!this.ctx.settings.motionBlur;
    const gateTarget = wantBlur ? Math.min(1, Math.max(0, (angular - 0.45) / 1.9)) : 0;
    this._blurGate += (gateTarget - this._blurGate) * Math.min(1, dt * 10);
    if (this._blurGate < 0.004) this._blurGate = 0;

    const uniforms = this.motionBlurEffect.uniforms;
    uniforms.get('inverseViewProjection').value.copy(_viewProj).invert();
    uniforms.get('previousViewProjection').value.copy(this._hasPrevFrame ? _prevViewProj : _viewProj);
    // 0.55 equivale a um obturador de ~180 graus: o rastro cobre pouco mais de
    // meio frame de movimento. Acima disso vira efeito, nao sensacao.
    uniforms.get('blurScale').value = this._blurGate * 0.55;

    // Passe desligado quando nao ha rastro nenhum: custo zero parado. Durante o
    // aquecimento ele fica ligado (com blurScale ~0) so para o shader compilar
    // no carregamento - e so se a qualidade atual for usar motion blur.
    const shouldRun = this._frames > WARMUP_FRAMES ? wantBlur && this._blurGate > 0.004 : wantBlur;
    if (this.motionBlurPass.enabled !== shouldRun) {
      this.motionBlurPass.enabled = shouldRun;
      this._syncRenderToScreen();
    }

    this._hasPrevFrame = true;
  }

  /**
   * Foco automatico do DOF: distancia ate o que esta na mira.
   *
   * O raycast roda no maximo ~12 vezes por segundo e so com o jogador mirando -
   * foco de camera e lento por natureza, nao precisa de precisao por frame.
   */
  _updateFocus(dt) {
    const settings = this.ctx.settings;
    const wantDof = !!settings.depthOfField && this._ads > 0.01;

    if (wantDof) {
      this._focusTimer -= dt;
      if (this._focusTimer <= 0) {
        this._focusTimer = 0.08;
        this._focusTarget = this._probeFocusDistance();
      }
      this._focus += (this._focusTarget - this._focus) * Math.min(1, dt * 5);

      const cocMaterial = this.dofEffect.cocMaterial;
      cocMaterial.focusDistance = this._focus;
      // Faixa nitida proporcional a distancia: perto tudo e raso, longe quase
      // tudo entra em foco - que e como lente de verdade se comporta.
      cocMaterial.focusRange = Math.min(24, Math.max(2.5, this._focus * 0.45));
      // Desfoque leve, e ainda multiplicado pela transicao de mira.
      this.dofEffect.bokehScale = 1.35 * this._ads;
    }

    const shouldRun = this._frames > WARMUP_FRAMES ? wantDof : !!settings.depthOfField;
    if (this.dofPass.enabled !== shouldRun) {
      this.dofPass.enabled = shouldRun;
      if (!shouldRun) this.dofEffect.bokehScale = 0;
      this._syncRenderToScreen();
    }
  }

  /** Distancia ate a geometria no centro da tela, com folga se nao houver nada. */
  _probeFocusDistance() {
    const collision = this.ctx.collision;
    const camera = this.ctx.camera;
    if (!collision || typeof collision.raycast !== 'function' || !camera) return FOCUS_FALLBACK;

    _origin.copy(camera.position);
    camera.getWorldDirection(_dir);
    const hit = collision.raycast(_origin, _dir, FOCUS_RANGE_MAX);
    if (hit && typeof hit.distance === 'number' && hit.distance > 0.2) return hit.distance;
    return FOCUS_FALLBACK;
  }

  /** Desloca o grao a cada frame. Grao parado parece sujeira na tela. */
  _updateGrain() {
    const offset = this.gradeEffect.uniforms.get('grainOffset').value;
    offset.set(Math.random() * 512, Math.random() * 512);
  }

  // ---------------------------------------------------------------------------
  // Configuracao
  // ---------------------------------------------------------------------------

  /**
   * Liga/desliga efeitos conforme o preset de qualidade.
   *
   * O composer NUNCA e recriado aqui: ligar e desligar passe e so um booleano, e
   * a fila continua a mesma. So a contagem de amostras (SSAO e motion blur)
   * recompila shader, e isso acontece exclusivamente na troca de preset, nunca
   * durante o jogo.
   */
  _applySettings() {
    if (!this.composer) return;
    try {
      const s = this.ctx.settings;

      const ssaoOn = !!s.ssao;
      this.normalPass.enabled = ssaoOn;
      this.ssaoPass.enabled = ssaoOn;
      if (ssaoOn) {
        const samples = s.quality === 'ultra' ? 16 : s.quality === 'high' ? 12 : 9;
        if (this.ssaoEffect.samples !== samples) this.ssaoEffect.samples = samples;
        // Buffer de AO em resolucao cheia de high para cima: com o raio corrigido
        // a oclusao passou a ter estrutura fina (a faixa de contato na base de um
        // caixote tem ~15 cm), e meia resolucao borrava justamente essa faixa
        // contra o piso. Em low/medium continua pela metade - la o preco do
        // preenchimento pesa mais que a nitidez da sombra de contato.
        const scale = s.quality === 'ultra' || s.quality === 'high' ? 1.0 : 0.5;
        if (this.ssaoEffect.resolution.scale !== scale) this.ssaoEffect.resolution.scale = scale;
      }

      this.bloomPass.enabled = s.bloom !== false;

      // Menos amostras de rastro na qualidade baixa (recompila o shader, mas so
      // acontece na troca de preset).
      const mbSamples = s.quality === 'low' || s.quality === 'medium' ? 5 : 9;
      if (this.motionBlurEffect.defines.get('MB_SAMPLES') !== mbSamples.toFixed(0)) {
        this.motionBlurEffect.samples = mbSamples;
      }
      if (!s.motionBlur) {
        this.motionBlurPass.enabled = false;
        this.motionBlurEffect.uniforms.get('blurScale').value = 0;
        this._blurGate = 0;
      }

      if (!s.depthOfField) {
        this.dofPass.enabled = false;
        this.dofEffect.bokehScale = 0;
      }

      const useFxaa = s.antialias === 'fxaa';
      const useSmaa = s.antialias === 'smaa' || (!useFxaa && s.antialias !== 'none');
      this.fxaaPass.enabled = useFxaa;
      this.smaaPass.enabled = useSmaa;
      if (useSmaa) {
        const preset =
          s.quality === 'ultra' ? SMAAPreset.ULTRA : s.quality === 'low' ? SMAAPreset.MEDIUM : SMAAPreset.HIGH;
        this.smaaEffect.applyPreset(preset);
        // O preset HIGH usa limiar de deteccao 0.10, que perde borda de baixo
        // contraste - barra clara de guarda-corpo contra reboco claro, fio fino
        // contra ceu. 0.05 (o limiar do preset ULTRA) faz o detector marcar essas
        // bordas mantendo os passos de busca do HIGH, que sao o caro do SMAA.
        // Nao recupera geometria sub-pixel, mas tira a serrilha grossa das que
        // ainda cobrem um pixel. Troca de define: recompila, e so acontece aqui,
        // na troca de preset, nunca por frame.
        if (s.quality !== 'low') {
          const material = this.smaaEffect.edgeDetectionMaterial;
          if (material && material.edgeDetectionThreshold !== 0.05) {
            material.edgeDetectionThreshold = 0.05;
          }
        }
      }

      // Grao um pouco mais fino quando a imagem e renderizada abaixo da
      // resolucao da tela (o upscale ja engrossa o ruido).
      const grain = (s.resolutionScale || 1) < 1 ? 0.022 : 0.030;
      this.gradeEffect.uniforms.get('grainAmount').value = grain;

      this._syncRenderToScreen();
    } catch (err) {
      this._warnOnce(err);
    }
  }

  /**
   * Garante que o ULTIMO passe habilitado escreva na tela. Sem isso, desligar um
   * passe do fim da cadeia deixaria a imagem presa em um alvo interno (tela
   * preta) - e o unico passe que converte de linear para sRGB e o que renderiza
   * para a tela.
   */
  _syncRenderToScreen() {
    let last = null;
    for (let i = 0; i < this._screenCandidates.length; i++) {
      const pass = this._screenCandidates[i];
      if (pass.enabled) last = pass;
    }
    // outputPass esta sempre ligado, entao "last" nunca fica nulo; a guarda
    // existe para nao apagar a tela se alguem desligar tudo em tempo de execucao.
    if (last === null) last = this.outputPass;
    for (let i = 0; i < this._passes.length; i++) {
      const pass = this._passes[i];
      const toScreen = pass === last;
      if (pass.renderToScreen !== toScreen) pass.renderToScreen = toScreen;
    }
  }

  onResize(width, height) {
    if (!this.composer) return;
    try {
      // O Engine ja chama composer.setSize antes deste onResize; repetir e
      // barato (os alvos ignoram tamanho igual) e protege o caso de um resize
      // vindo por outro caminho, como troca de escala de resolucao.
      this.composer.setSize(width, height);
    } catch (err) {
      this._warnOnce(err);
    }
  }

  _warnOnce(err) {
    if (this._warned) return;
    this._warned = true;
    console.warn('[PostFX] degradando em silencio apos falha:', err);
  }

  dispose() {
    if (this._offSettings) {
      this._offSettings();
      this._offSettings = null;
    }
    if (this.composer) {
      this.composer.dispose();
      this.composer = null;
    }
    this._passes.length = 0;
  }
}
